import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert } from '@/components/ui';
import { SimpleInput } from '@/components/ui/SimpleInput';
import { SimpleTextarea } from '@/components/ui/SimpleTextarea';
import { SimpleSwitch } from '@/components/ui/SimpleSwitch';
import { useUserGroupsStore } from '@/store/userGroupsStore';
import type { UserGroup, CreateUserGroupRequest } from '@/types/userGroups.types';

interface UserGroupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userGroup?: UserGroup;
  onSuccess: (operation: 'created' | 'updated', userGroupName: string) => void;
}

interface FormData {
  name: string;
  code: string;
  description: string;
  isActive: boolean;
}

interface FormErrors {
  name?: string;
  code?: string;
}

/**
 * UserGroupDialog - Modal component for creating and editing user groups
 */
export const UserGroupDialog: React.FC<UserGroupDialogProps> = ({
  isOpen,
  onClose,
  userGroup,
  onSuccess,
}) => {
  const { createUserGroup, updateUserGroup, isCreating, isUpdating } = useUserGroupsStore();

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    code: '',
    description: '',
    isActive: true,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isProcessing = isCreating || isUpdating;
  const isEditing = !!userGroup;

  // Initialize form data when dialog opens or userGroup changes
  useEffect(() => {
    if (isOpen) {
      if (userGroup) {
        setFormData({
          name: userGroup.name,
          code: userGroup.code,
          description: userGroup.description || '',
          isActive: userGroup.isActive,
        });
      } else {
        setFormData({
          name: '',
          code: '',
          description: '',
          isActive: true,
        });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, userGroup]);

  // Handle input changes
  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear field error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }

    // Clear submit error
    if (submitError) {
      setSubmitError(null);
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Group Name is required.';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Group Code is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || isProcessing) {
      return;
    }

    try {
      const requestData: CreateUserGroupRequest = {
        name: formData.name.trim(),
        code: formData.code.trim(),
        description: formData.description.trim(),
        isActive: formData.isActive,
      };

      if (isEditing && userGroup) {
        await updateUserGroup(userGroup.id, requestData);
        onSuccess('updated', formData.name.trim());
      } else {
        await createUserGroup(requestData);
        onSuccess('created', formData.name.trim());
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setSubmitError(errorMessage);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? `EDIT ${userGroup?.name} GROUP` : 'CREATE NEW GROUP'}
      className="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Submit Error Alert */}
        {submitError && (
          <Alert variant="error" onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        {/* Form Fields */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Group Name */}
          <SimpleInput
            id="name"
            type="text"
            label="Group Name"
            required
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={errors.name}
            placeholder="Enter group name"
            disabled={isProcessing}
          />

          {/* Group Code */}
          <SimpleInput
            id="code"
            type="text"
            label="Group Code"
            required
            value={formData.code}
            onChange={(e) => handleInputChange('code', e.target.value)}
            error={errors.code}
            placeholder="Enter group code"
            disabled={isProcessing || isEditing} // Disable code editing for existing groups
          />
        </div>

        {/* Description */}
        <SimpleTextarea
          id="description"
          label="Description"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Enter group description"
          rows={4}
          disabled={isProcessing}
        />

        {/* Is Active Switch */}
        <SimpleSwitch
          checked={formData.isActive}
          onChange={(checked: boolean) => handleInputChange('isActive', checked)}
          label="Is Active?"
          disabled={isProcessing}
        />

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isProcessing}
            loading={isProcessing}
            rightIcon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            }
          >
            {isEditing ? 'Save Changes' : 'Create Group'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
